import React, { useState } from 'react';
import styled from 'styled-components';
import { Download, Eye, FileText, CheckCircle, Clock, Lock, FileType } from 'lucide-react';
import { ContractDocumentForClientDto, contractApi, ImageConversionResultDto } from '../../services/contractService';
import ContractDocumentViewModal from './ContractDocumentViewModal';
import ConfirmationModal from './ConfirmationModal';
import { toast } from 'react-hot-toast';
import { handleApiError } from '../../utils/errorHandler';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const Title = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ReadOnlyBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.1);
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 500;
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const DocumentCard = styled.div`
  background: white;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 16px;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-color: rgba(124, 58, 237, 0.3);
  }
`;

const DocumentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const DocumentIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(59, 130, 246, 0.1));
  border-radius: var(--border-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c3aed;
  flex-shrink: 0;
`;

const DocumentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocumentName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
`;

const DocumentMeta = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusBadge = styled.span<{ signed: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => props.signed ? 'rgba(22, 163, 74, 0.1)' : 'rgba(217, 119, 6, 0.1)'};
  color: ${props => props.signed ? '#16a34a' : '#d97706'};
`;

const SignatureSection = styled.div`
  background: rgba(124, 58, 237, 0.03);
  border-radius: var(--border-radius-sm);
  padding: 12px;
  border: 1px dashed rgba(124, 58, 237, 0.2);
`;

const SignatureLabel = styled.div`
  font-size: 11px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  font-weight: 500;
`;

const SignaturePreview = styled.img`
  max-width: 100%;
  max-height: 60px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: white;
`;

const NoSignature = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  font-style: italic;
`;

const DocumentActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: #7c3aed;
    color: white;
    border: none;

    &:hover {
      background: #6d28d9;
    }
  ` : `
    background: transparent;
    color: #7c3aed;
    border: 1px solid #7c3aed;

    &:hover {
      background: rgba(124, 58, 237, 0.05);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: var(--text-secondary);
`;

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper function to check if a URL points to an image file
const isImageFile = (url?: string): boolean => {
  if (!url) return false;
  const ext = url.toLowerCase().split('.').pop() || '';
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
};

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface ContractDocumentsGridProps {
  documents: ContractDocumentForClientDto[];
  contractNumber: string;
  onBack: () => void;
  onConvertToPdf?: (documentId: number) => Promise<ImageConversionResultDto>;
  onRefresh?: () => void;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ContractDocumentsGrid: React.FC<ContractDocumentsGridProps> = ({
  documents,
  contractNumber,
  onBack,
  onConvertToPdf,
  onRefresh,
}) => {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<ContractDocumentForClientDto | null>(null);
  const [isConverting, setIsConverting] = useState(false);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    onConfirm: () => void;
    isLoading?: boolean;
    confirmText?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'default',
    onConfirm: () => {},
    isLoading: false,
    confirmText: 'אישור',
  });

  // Alert modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'default',
  });

  const showAlert = (title: string, message: string, variant: 'default' | 'danger' = 'default') => {
    setAlertModal({ isOpen: true, title, message, variant });
  };

  const handleDownload = async (documentId: number) => {
    setDownloadingId(documentId);
    try {
      const blob = await contractApi.downloadDocumentPdf(documentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `document_${documentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בהורדת המסמך'));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleView = (document: ContractDocumentForClientDto) => {
    setSelectedDocument(document);
  };

  const handleConvertToPdf = (doc: ContractDocumentForClientDto) => {
    if (!onConvertToPdf) return;

    setConfirmModal({
      isOpen: true,
      title: 'המרת תמונה ל-PDF',
      message: `האם אתה בטוח שברצונך להמיר את המסמך לקובץ PDF?\n\n⚠️ פעולה זו תחליף את הקובץ המקורי ולא ניתן לבטלה.`,
      variant: 'danger',
      isLoading: false,
      confirmText: 'המר ל-PDF',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        setIsConverting(true);
        try {
          const result = await onConvertToPdf(doc.id);
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          setIsConverting(false);
          if (result.success) {
            showAlert('הומר בהצלחה', `המסמך הומר ל-PDF בהצלחה`);
            if (onRefresh) {
              onRefresh();
            }
          } else {
            showAlert('שגיאה בהמרה', result.errorMessage || 'שגיאה לא צפויה בהמרת הקובץ', 'danger');
          }
        } catch (error: any) {
          console.error('Convert error:', error);
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          setIsConverting(false);
          showAlert('שגיאה', error?.response?.data?.error || 'שגיאה בהמרת המסמך ל-PDF', 'danger');
        }
      },
    });
  };

  if (documents.length === 0) {
    return (
      <Container>
        <EmptyState>
          <FileText size={48} strokeWidth={1.5} style={{ marginBottom: 16, opacity: 0.5 }} />
          <p>אין מסמכי חוזה</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          מסמכי חוזה {contractNumber && `(${contractNumber})`}
        </Title>
        <ReadOnlyBadge>
          <Lock size={12} />
          קריאה בלבד
        </ReadOnlyBadge>
      </Header>

      <DocumentsGrid>
        {documents.map(doc => (
          <DocumentCard key={doc.id}>
            <DocumentHeader>
              <DocumentIcon>
                <FileText size={20} />
              </DocumentIcon>
              <DocumentInfo>
                <DocumentName>{doc.documentTypeName}</DocumentName>
                <DocumentMeta>
                  <StatusBadge signed={doc.isSigned}>
                    {doc.isSigned ? (
                      <>
                        <CheckCircle size={12} />
                        חתום
                      </>
                    ) : (
                      <>
                        <Clock size={12} />
                        ממתין לחתימה
                      </>
                    )}
                  </StatusBadge>
                  {doc.manuallyUploaded && (
                    <span style={{ fontSize: 11, color: '#6b7280' }}>
                      (הועלה ידנית)
                    </span>
                  )}
                </DocumentMeta>
              </DocumentInfo>
            </DocumentHeader>

            {doc.isSigned && doc.signedAt && (
              <DocumentMeta>
                נחתם: {formatDate(doc.signedAt)}
              </DocumentMeta>
            )}

            {doc.hasSignature && doc.signatureData && (
              <SignatureSection>
                <SignatureLabel>חתימה:</SignatureLabel>
                <SignaturePreview
                  src={doc.signatureData}
                  alt="חתימת לקוח"
                />
              </SignatureSection>
            )}

            {doc.hasMotherSignature && doc.motherSignatureData && (
              <SignatureSection>
                <SignatureLabel>חתימת האם:</SignatureLabel>
                <SignaturePreview
                  src={doc.motherSignatureData}
                  alt="חתימת האם"
                />
                {doc.motherSignedAt && (
                  <DocumentMeta style={{ marginTop: 8 }}>
                    נחתם: {formatDate(doc.motherSignedAt)}
                  </DocumentMeta>
                )}
              </SignatureSection>
            )}

            {doc.isSigned && !doc.hasSignature && !doc.manuallyUploaded && (
              <SignatureSection>
                <NoSignature>חתימה דיגיטלית לא זמינה</NoSignature>
              </SignatureSection>
            )}

            <DocumentActions>
              <ActionButton
                variant="secondary"
                onClick={() => handleView(doc)}
              >
                <Eye size={16} />
                צפה
              </ActionButton>
              {onConvertToPdf && doc.manuallyUploaded && isImageFile(doc.manualUploadUrl) && (
                <ActionButton
                  variant="secondary"
                  onClick={() => handleConvertToPdf(doc)}
                  disabled={isConverting}
                >
                  <FileType size={16} />
                  {isConverting ? 'ממיר...' : 'המר ל-PDF'}
                </ActionButton>
              )}
              <ActionButton
                variant="primary"
                onClick={() => handleDownload(doc.id)}
                disabled={downloadingId === doc.id}
              >
                <Download size={16} />
                {downloadingId === doc.id ? 'מוריד...' : 'הורד PDF'}
              </ActionButton>
            </DocumentActions>
          </DocumentCard>
        ))}
      </DocumentsGrid>

      {selectedDocument && (
        <ContractDocumentViewModal
          document={selectedDocument}
          onClose={() => setSelectedDocument(null)}
          onDownload={() => handleDownload(selectedDocument.id)}
          isDownloading={downloadingId === selectedDocument.id}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText || 'אישור'}
        isLoading={confirmModal.isLoading}
      />

      {/* Alert Modal */}
      <ConfirmationModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        confirmText="סגור"
        cancelText=""
      />
    </Container>
  );
};

export default ContractDocumentsGrid;
